// Enrolment for POST /referral/enroll, from an email link like https://diffit.me/?enroll=<surface>
// (see referralAttribution.js for the sibling `?ref=`/`?i=` share-link flow).
//
// The click and the enrolling request can be a page load or two apart (the visitor still has
// to sign in), so this persists to localStorage rather than sessionStorage. No expiry window,
// unlike attribution's 90 days: the record is cleared on the first completed request after
// sign-in (see the drain in Base), and the backend's own liveness check already refuses to
// enrol anyone into an ended campaign.
//
// Every storage access is wrapped in try/catch: storage throws in private-mode/blocked-
// cookie browsers, and that must never break sign-in.

const STORAGE_KEY = 'diffitReferralEnrolment';

// Reads `enroll` off the current URL and persists the surface it names. Safe to call on any
// page load: when the param is absent this is a no-op that leaves any previously stored
// surface alone, rather than clobbering it with an empty capture.
export function captureEnrolmentFromUrl(search = window.location.search) {
  try {
    const params = new URLSearchParams(search);
    const surface = params.get('enroll');
    if (!surface) return null;

    const record = { surface, capturedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    return record;
  } catch {
    return null;
  }
}

// The stored surface name, or null when nothing is stored, the blob is malformed, or `surface`
// isn't a non-empty string.
export function readEnrolmentSurface() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const { surface } = JSON.parse(raw);
    return typeof surface === 'string' && surface ? surface : null;
  } catch {
    return null;
  }
}

// Forget the stored capture. Call this once the record has been handed to POST
// /referral/enroll — on a completed request, not a successful enrolment (see the drain in
// Base for why).
export function clearEnrolmentSurface() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode / blocked cookies — nothing was stored, so nothing to forget.
  }
}
